import { useEffect, useState } from "react";
import { SideBar } from "./inc/sideBar";
import { TopHeader } from "./inc/topHeader";
import { jwtDecode } from "jwt-decode";
import { getStudentByEmail } from "../../services/StudentService";
import { getStudyCountByStudentId } from "../../services/StudyService";
import {
  getLatest5ProgressByStudentId,
  getProgressCountByStudentId,
} from "../../services/ProgressService";
import Chart from "react-apexcharts";

const StudentHome = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeMenu, setActiveMenu] = useState(null);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );

  const [studyCount, setStudyCount] = useState(0);
  const [progressCount, setProgressCount] = useState(0);
  const [studentId, setStudentId] = useState(null);
  const [progressList, setProgressList] = useState([]);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (windowWidth < 768) {
      setSidebarOpen(false);
    }
  }, [windowWidth]);

  const toggleMenu = (menu) => {
    setActiveMenu(activeMenu === menu ? null : menu);
  };

  // 2 study , master and phd
  const studyPercentage = 2 > 0 ? Math.round((studyCount / 2) * 100) : 0;

  // 24 = 2 years
  const progressPercentage =
    24 > 0 ? Math.round((progressCount / 24) * 100) : 0;

  const token = localStorage.getItem("token");
  const userEmail = jwtDecode(token).username;

  useEffect(() => {
    if (userEmail) {
      getStudentByEmail(userEmail)
        .then((res) => setStudentId(res.data.data.student_id))        
        .catch((err) => console.error("Error fetching student by email", err));
    }
  }, [userEmail]);

  useEffect(() => {
    if (studentId) {
      getStudyCountByStudentId(studentId)
        .then((res) => setStudyCount(res.data.total))
        .catch((err) => console.error("Study count error:", err));

      getProgressCountByStudentId(studentId)
        .then((res) => setProgressCount(res.data.data))
        .catch((err) => console.error("Progress count error:", err));

      getLatest5ProgressByStudentId(studentId)
        .then((res) => setProgressList(res.data))
        .catch((err) => console.error("Progress list error:", err));
    }
  }, [studentId]);

  const getProgressLabelAndClass = (dateStr) => {
    if (!dateStr)
      return {
        label: "Unknown Submission",
        className: "feed-item feed-item-secondary",
      };
    const date = new Date(dateStr);
    const day = date.getDate();

    if (day >= 1 && day <= 10) {
      return {
        label: "Early Submission",
        className: "feed-item feed-item-success",
      };
    } else if (day >= 11 && day <= 25) {
      return {
        label: "On-Time Submission",
        className: "feed-item feed-item-secondary",
      };
    } else {
      return {
        label: "End-of-Month Submission",
        className: "feed-item feed-item-danger",
      };
    }
  };

  const getStudyEvaluation = () => {
    if (!progressList || progressList.length === 0) {
      return {
        status: "No Progress Data",
        className: "bg-secondary",
        percentage: 0,
      };
    }

    const conditions = progressList.map((progress) => {
      const isMeetProgressPlanYes = progress.meet_progress_plan === "YES";
      const isSubmitThesisOnTime =
        progress.submit_thesis &&
        new Date(progress.submit_thesis) <=
          new Date(progress.studyId.finish_date);
      const isStudyCompletionOnTime =
        progress.study_complition &&
        new Date(progress.study_complition) <=
          new Date(progress.studyId.finish_date);

      const metCount = [
        isMeetProgressPlanYes,
        isSubmitThesisOnTime,
        isStudyCompletionOnTime,
      ].filter(Boolean).length;

      return metCount;
    });

    const averageMet =
      conditions.length > 0
        ? Math.round(
            (conditions.reduce((a, b) => a + b, 0) / conditions.length / 3) *
              100
          )
        : 0;

    let status, className;

    if (averageMet === 100) {
      status = "Good Progress";
      className = "bg-success";
    } else if (averageMet >= 67) {
      status = "Moderate Progress";
      className = "bg-warning";
    } else if (averageMet > 0) {
      status = "Poor Progress";
      className = "bg-danger";
    } else {
      status = "No Progress Data";
      className = "bg-secondary";
    }

    return { status, className, percentage: averageMet };
  };

  const calculateProgressPercentage = (progress) => {
    const isMeetProgressPlanYes = progress.meet_progress_plan === "YES";
    const isSubmitThesisOnTime =
      progress.submit_thesis &&
      new Date(progress.submit_thesis) <= new Date(progress.studyId.finish_date);
    const isStudyCompletionOnTime =
      progress.study_complition &&
      new Date(progress.study_complition) <=
        new Date(progress.studyId.finish_date);

    const metCount = [
      isMeetProgressPlanYes,
      isSubmitThesisOnTime,
      isStudyCompletionOnTime,
    ].filter(Boolean).length;

    return Math.round((metCount / 3) * 100);
  };
  const { status, className, percentage } = getStudyEvaluation();

  const getChartColor = (percentage) => {
    if (percentage >= 75) return "#28a745"; // green for good
    if (percentage >= 40) return "#ffc107"; // yellow for moderate
    return "#dc3545"; // red for poor
  };

  const renderStars = (percentage) => {
    const yellowColor = "#ffc107";
    const grayColor = "#ddd";

    if (percentage === 100) {
      return (
        <div>
          {[...Array(5)].map((_, i) => (
            <i
              key={i}
              className="fas fa-star"
              style={{ color: yellowColor }}
            ></i>
          ))}
        </div>
      );
    } else if (percentage >= 67) {
      return (
        <div>
          {[...Array(2)].map((_, i) => (
            <i
              key={i}
              className="fas fa-star"
              style={{ color: yellowColor }}
            ></i>
          ))}
          <i
            className="fas fa-star-half-alt"
            style={{ color: yellowColor }}
          ></i>
          {[...Array(2)].map((_, i) => (
            <i
              key={i + 3}
              className="far fa-star"
              style={{ color: grayColor }}
            ></i>
          ))}
        </div>
      );
    } else if (percentage > 0) {
      return (
        <div>
          <i className="fas fa-star" style={{ color: yellowColor }}></i>
          {[...Array(4)].map((_, i) => (
            <i
              key={i + 1}
              className="far fa-star"
              style={{ color: grayColor }}
            ></i>
          ))}
        </div>
      );
    } else {
      return (
        <div>
          {[...Array(5)].map((_, i) => (
            <i key={i} className="far fa-star" style={{ color: grayColor }}></i>
          ))}
        </div>
      );
    }
  };

  return (
    <>
      {/* <div className="wrapper"> */}
      <div className="d-flex position-relative" style={{ minHeight: "100vh" }}>
        <SideBar
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          activeMenu={activeMenu}
          toggleMenu={toggleMenu}
          windowWidth={windowWidth}
        />
        <div className="main-panel">
          <TopHeader
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            windowWidth={windowWidth}
          />
          <div className="container mt-0">
            <div className="page-inner">
              <div className="row">
                <div className="col-12 col-sm-6 col-md-6 col-xl-3">
                  <div className="card zis-shadow rounded-2">
                    <div className="card-body">
                      <div className="d-flex justify-content-between">
                        <div>
                          <h5>
                            <b>Total Study</b>
                          </h5>
                          <p className="text-muted">All study value</p>
                        </div>
                        <h3 className="text-info fw-bold fs-1">{studyCount}</h3>
                      </div>

                      <div className="progress progress-sm">
                        <div
                          className="progress-bar bg-info"
                          role="progressbar"
                          style={{ width: `${studyPercentage}%` }}
                          aria-valuenow={studyPercentage}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        />
                      </div>

                      <div className="d-flex justify-content-between mt-2">
                        <p className="text-muted mb-0">Completed Studies</p>
                        <p className="text-muted mb-0">{studyPercentage}%</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-12 col-sm-6 col-md-6 col-xl-3">
                  <div className="card zis-shadow rounded-2">
                    <div className="card-body">
                      <div className="d-flex justify-content-between">
                        <div>
                          <h5>
                            <b>Study Progress</b>
                          </h5>
                          <p className="text-muted">All progress value</p>
                        </div>
                        <h3 className="text-success fw-bold fs-1">
                          {progressCount}
                        </h3>
                      </div>

                      <div className="progress progress-sm">
                        <div
                          className="progress-bar bg-success"
                          role="progressbar"
                          style={{ width: `${progressPercentage}%` }}
                          aria-valuenow={progressPercentage}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        />
                      </div>

                      <div className="d-flex justify-content-between mt-2">
                        <p className="text-muted mb-0">Progress Achieved</p>
                        <p className="text-muted mb-0">{progressPercentage}%</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-12 col-sm-6 col-md-6 col-xl-3">
                  <div className="card zis-shadow rounded-2">
                    <div className="card-body">
                      <div className="d-flex justify-content-between">
                        <div>
                          <h5>
                            <b>Study evaluation</b>
                          </h5>
                          <p className="text-muted">
                            Specific evaluation value
                          </p>
                        </div>
                      </div>
                      <div className="progress progress-sm">
                        <div
                          className={`progress-bar ${className}`}
                          role="progressbar"
                          style={{ width: `${percentage}%` }}
                          aria-valuenow={percentage}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        />
                      </div>
                      <div className="d-flex justify-content-between mt-2">
                        <p className="text-muted mb-0">
                          Progress study evaluation
                        </p>
                        <p className="text-muted mb-0">{percentage}%</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-12 col-sm-6 col-md-6 col-xl-3">
                  <div className="card zis-shadow rounded-2">
                    <div className="card-body">
                      <div className="d-flex justify-content-between">
                        <div>
                          <h5>
                            <b>Evaluation Status</b>
                          </h5>
                          <p className="text-muted">
                            Specific evaluation status
                          </p>
                        </div>
                      </div>

                      <div className="progress progress-sm">
                        <div
                          className={`progress-bar ${className}`}
                          role="progressbar"
                          style={{ width: `${percentage}%` }}
                          aria-valuenow={percentage}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        />
                      </div>

                      <div className="d-flex justify-content-between mt-2">
                        <p className="text-muted mb-0">
                          Study evaluation status
                        </p>
                        <p className="text-muted mb-0">
                          <span
                            className={`fw-bold badge ${
                              className === "bg-success"
                                ? "badge-success"
                                : className === "bg-warning"
                                ? "badge-warning"
                                : "badge-danger"
                            }`}
                          >
                            {status}
                          </span>
                        </p>
                        <p className="text-muted mb-0">{percentage}%</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="card zis-shadow rounded-0">
                    <div className="card-header">
                      <h5 className="fw-bold mb-0">Current Month Progress</h5>
                    </div>

                    <div className="card-body d-flex flex-column align-items-center justify-content-center">
                      <div style={{ width: "200px", height: "200px" }}>
                        <Chart
                          options={{
                            chart: { type: "radialBar" },
                            plotOptions: {
                              radialBar: {
                                hollow: { size: "70%" },
                                dataLabels: {
                                  name: { show: false },
                                  value: {
                                    show: true,
                                    fontSize: "24px",
                                    fontWeight: "bold",
                                    color: getChartColor(percentage),
                                    offsetY: 5,
                                    formatter: (val) => `${val}%`,
                                  },
                                },
                              },
                            },
                            colors: [getChartColor(percentage)],
                          }}
                          series={[percentage]}
                          type="radialBar"
                          height={200}
                        />
                      </div>

                      <div className="mt-3 fs-4">{renderStars(percentage)}</div>

                      <p className="text-muted text-center mt-3 mb-0">
                        Based on your current progress, your overall study
                        evaluation is <strong>{status}</strong> with{" "}
                        <strong>{percentage}%</strong> achievement.
                      </p>

                      <div className="progress w-100 mt-3">
                        <div
                          className={`progress-bar ${className}`}
                          role="progressbar"
                          style={{
                            width: `${percentage}%`,
                            transition: "width 1s ease",
                          }}
                          aria-valuenow={percentage}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="card zis-shadow rounded-0">
                    <div className="card-header">
                      <div className="card-head-row card-tools-still-right">
                        <div className="card-title">Recent Progress</div>
                      </div>
                    </div>
                    <div className="card-body">
                      <ol className="activity-feed">
                        {progressList.length === 0 && (
                          <li>No progress records found.</li>
                        )}
                        {progressList.map((progress) => {
                          const { label, className } = getProgressLabelAndClass(
                            progress.progress_date
                          );
                          const dateFormatted = new Date(
                            progress.progress_date
                          ).toLocaleDateString(undefined, {
                            year: "numeric",
                            month: "short",
                            day: "2-digit",
                          });

                          const progressPercentage =
                            calculateProgressPercentage(progress);
                          let badgeClass = "badge ";
                          if (progressPercentage === 100) {
                            badgeClass += "badge-success";
                          } else if (progressPercentage >= 67) {
                            badgeClass += "badge-warning";
                          } else {
                            badgeClass += "badge-danger";
                          }

                          return (
                            <li key={progress.progressId} className={className}>
                              <div className="d-flex justify-content-between">
                                <div>
                                  <time className="date fw-bold fst-italic">
                                    {dateFormatted}
                                  </time>
                                  <span className="text">{label}</span>
                                </div>
                                <span
                                  className={`fw-bold text-black badge rounded-pill ${badgeClass}`}
                                >
                                  {progressPercentage}%
                                </span>
                              </div>
                            </li>
                          );
                        })}
                      </ol>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default StudentHome;
